import { sb } from '../supabase';
import { getTrackingUrl } from './tracking';
import { isRequestOfferable } from './requestDistribution';
import { buildThumbResolver } from './requestAttachmentUrls';
import { withContactPhones } from './requestContacts';
import { fetchRequestFees, fetchRequestFee, isFeeSettled } from './requestFee';

// Managed-order data layer — the trader's tracker feed + the admin's lifecycle
// controls. The offer/deposit reuse order_invoices (the premium invoice + Telr).

export async function fetchManagedTracker(requestId) {
  const [reqRes, evRes, invRes] = await Promise.all([
    sb.from('requests')
      // reference_image is deliberately absent: ManagedOrder.jsx renders no image
      // at all, so selecting it fetched a column nothing read.
      .select('id, request_ref, title_ar, title_en, title_zh, quantity, unit, managed_status, status, created_at, assigned_to, budget_per_unit, budget_currency, payment_plan, tracking_number, shipping_company, tracking_url')
      .eq('id', requestId).maybeSingle(),
    sb.from('managed_events').select('*').eq('request_id', requestId).order('created_at', { ascending: true }),
    sb.from('order_invoices').select('*').eq('request_id', requestId).order('created_at', { ascending: false }).limit(1),
  ]);
  return { request: reqRes.data || null, events: evRes.data || [], offer: (invRes.data && invRes.data[0]) || null };
}

// Admin console: all managed orders (list) + one order's full detail.
//
// Each row carries a resolved `thumb_url`: a signed URL for its first image
// attachment, falling back to the legacy public reference_image so rows created
// before that write was retired keep their picture. reference_image is still
// selected FOR that fallback — it is the fallback, not the primary.
export async function fetchManagedOrders() {
  const { data } = await sb.from('requests')
    .select('id, request_ref, title_ar, title_en, managed_status, status, created_at, last_activity_at, quantity, unit, budget_per_unit, budget_currency, contact_phone, reference_image, attachment_paths, requester:profiles!requests_buyer_id_fkey(full_name, company_name, whatsapp)')
    .eq('sourcing_mode', 'managed').order('created_at', { ascending: false });

  // contact_phone is admin-only in request_contacts since 2026-09-28; the
  // overlay keeps r.contact_phone working in either deploy order.
  const rows = await withContactPhones(data || []);
  const thumb = await buildThumbResolver(rows, (r) => r.attachment_paths);
  // The request fee: an unpaid request is not the team's work yet, and the
  // list says so rather than showing it as a new request (rule 12, 2026-09-29).
  const feeBy = await fetchRequestFees(sb, rows.map((r) => r.id));
  return rows.map((r) => ({ ...r, thumb_url: thumb(r), fee: feeBy[r.id] || null }));
}

// ⚠️ `offerable` is computed HERE, in this screen's own data layer, off the RAW
// requests.status — the same contract consoleRequestDetail.js:114 and
// consoleQuotations.js:167 follow. It is not optional and it is not a detail:
// DistributePanel takes `offerable` as a prop defaulting to FALSE, so a caller
// that forgets it does not get a warning, it gets a panel that greys itself and
// says "هذا الطلب ملغي — لا يمكن توزيعه" on a perfectly healthy request.
//
// That is exactly what /admin2/managed/<id> did to M-10075 in production
// (status 'open'), two lines under a header reading "استلمنا طلبك". Added
// 2026-09-20 after the SECOND time this shape shipped: the first was
// ConsoleQuotations passing a board row whose `status` had been overwritten
// with derived vocabulary.
//
// The panel defaults to false ON PURPOSE — a greyed button on a live request is
// a complaint, a paid WhatsApp about a dead one is not recoverable. The cost of
// that choice is that every mount must pass the prop, and there are four of
// them (DistributePanel ×3, PublicLinkPanel ×1 — RoutePanel was folded into
// DistributePanel on 2026-10-12). If you add a fifth, it needs this line too.
export async function fetchManagedOrderAdmin(requestId) {
  const { data } = await sb.from('requests')
    .select('*, requester:profiles!requests_buyer_id_fkey(full_name, company_name, email, whatsapp)')
    .eq('id', requestId).maybeSingle();
  if (!data) return null;
  // Admin-only phone from request_contacts (2026-09-28), overlaid.
  const [withPhone] = await withContactPhones([data]);
  // Offerable also needs the request fee settled — the same AND the server's
  // request_is_offerable() does (2026-09-29).
  const fee = await fetchRequestFee(sb, requestId);
  return { ...withPhone, fee, offerable: isRequestOfferable(data) && isFeeSettled(fee) };
}

export async function fetchAccountManager(userId) {
  if (!userId) return null;
  const { data } = await sb.from('profiles').select('full_name, avatar_url').eq('id', userId).maybeSingle();
  return data || null;
}

// ── Admin lifecycle controls ────────────────────────────────────────────────
export async function addManagedEvent({ requestId, stage, kind = 'note', title, body, mediaUrl, visibleToBuyer = true }) {
  const { data: { user } } = await sb.auth.getUser();
  const { data, error } = await sb.from('managed_events').insert({
    request_id: requestId, stage: stage || null, kind, title: title || null, body: body || null,
    media_url: mediaUrl || null, visible_to_buyer: visibleToBuyer, created_by: user?.id || null,
  }).select('*').single();
  if (error) throw error;
  return data;
}

// Admin presents the single curated offer (creates the managed order_invoice +
// flips managed_status to 'offer_ready'). sourceSupplierId records, admin-only
// (managed_offer_sources), which supplier the anonymised offer comes from: for a
// managed request the fee counts as earned only when that supplier is verified
// (request_fee_delivered). Omitted → the offer is issued but does not count.
export async function createManagedOffer({ requestId, lineItems, currency, factoryProfile, notes, sourceSupplierId }) {
  const { data, error } = await sb.rpc('create_managed_offer', {
    p_request_id: requestId, p_line_items: lineItems || [], p_currency: currency || 'USD',
    p_factory_profile: factoryProfile || null, p_notes: notes || null,
    ...(sourceSupplierId ? { p_source_supplier_id: sourceSupplierId } : {}),
  });
  if (error) throw error;
  return data;
}

// The same verified-equivalent set guard_offer_write and request_fee_delivered use.
export const VERIFIED_SUPPLIER_STATUSES = ['verified', 'approved', 'active'];
export const isVerifiedSupplierStatus = (status) =>
  VERIFIED_SUPPLIER_STATUSES.includes(String(status || '').toLowerCase());

// Supplier accounts an admin can name as a curated offer's source. Admin RLS
// reads profiles; verified ones first.
export async function fetchOfferSourceSuppliers() {
  const { data, error } = await sb.from('profiles')
    .select('id, company_name, full_name, maabar_supplier_id, status')
    .eq('role', 'supplier')
    .order('company_name', { ascending: true });
  if (error) throw error;
  const rows = data || [];
  return [...rows.filter((r) => isVerifiedSupplierStatus(r.status)),
          ...rows.filter((r) => !isVerifiedSupplierStatus(r.status))];
}

// All events for a request (admin sees non-buyer-visible ones too, via RLS).
export async function fetchManagedEvents(requestId) {
  const { data } = await sb.from('managed_events').select('*').eq('request_id', requestId).order('created_at', { ascending: true });
  return data || [];
}

/**
 * Advance a managed order to a stage: writes requests.managed_status AND the
 * managed_events row in ONE transaction, or neither.
 *
 * This replaced setManagedStage + addManagedEvent as separate calls. That pair
 * could not be atomic — two PostgREST calls are two transactions — and worse,
 * the first half was being discarded in silence: `requests` has no UPDATE
 * policy admitting an admin, so RLS matched zero rows, PostgREST answered 204,
 * and `error` was null. M-10067 sat at 'admin_review' through three advances
 * while three WhatsApp messages went out and were delivered.
 *
 * `status` stays the CALLER's to supply. STATUS_FOR_STAGE in managedStages.js
 * is the single owner of stage -> managed_status; putting that map in the
 * database would make a fourth copy of the stage list (see the comment at the
 * head of managedStages.js for the other three).
 */
export async function advanceManagedStage({ requestId, stage, status, title, kind = 'status', mediaUrl = null }) {
  const { data, error } = await sb.rpc('advance_managed_stage', {
    p_request_id: requestId,
    p_stage: stage,
    p_status: status,
    p_title: title ?? null,
    p_kind: kind,
    p_media_url: mediaUrl,
  });
  if (error) throw error;
  return data;
}

/**
 * Direct status write, with no event. Kept for callers that are NOT stage
 * advances; an advance must go through advanceManagedStage above.
 *
 * `.select('id')` is load-bearing, not decoration. Without it an update that
 * RLS filters to zero rows returns 204 and no error, and this function reports
 * success for a write that never happened — which is the exact defect above.
 */
export async function setManagedStage(requestId, managedStatus) {
  const { data, error } = await sb.from('requests')
    .update({ managed_status: managedStatus })
    .eq('id', requestId)
    .select('id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(`managed_status was not written for request ${requestId}: the update matched no row (row-level security, or the id does not exist).`);
  }
  return data[0];
}

// Shipment tracking for the out_for_delivery stage. Optional throughout — the
// stage advances with or without it. Reuses the request's existing tracking
// columns (the same ones the direct-order flow writes); `tracking_url` is the
// escape hatch for a local carrier no tracker covers.
//
// Writes ONLY the keys present in `patch`, so a field the admin did not touch is
// never included in the UPDATE and cannot be overwritten — if the form ever loads
// without its saved values, a save can no longer blank a real tracking number.
// Deliberate clearing still works: the caller includes the key with an empty
// string, which is stored as NULL.
export async function setManagedTracking(requestId, patch = {}) {
  const clean = (v) => {
    const s = String(v ?? '').trim();
    return s === '' ? null : s;
  };
  const cols = {};
  if ('trackingNumber' in patch) cols.tracking_number = clean(patch.trackingNumber);
  if ('shippingCompany' in patch) cols.shipping_company = clean(patch.shippingCompany);
  if ('trackingUrl' in patch) cols.tracking_url = clean(patch.trackingUrl);
  if (Object.keys(cols).length === 0) return false;

  // /track/:token reads ONE column — tracking_redirect_url — so it has to keep
  // reflecting the other three. Resolve it here, where getTrackingUrl already
  // lives: the database deliberately holds no copy of the carrier map (see
  // migration 20260914095916; src/lib/tracking.js notes the four copies that
  // already exist, and nobody wants a fifth in plpgsql).
  //
  // The patch is partial — only changed fields — so the saved row is read and
  // merged first. Computing from the patch alone would resolve a carrier URL
  // against a number the admin did not touch, or blank the destination whenever
  // only one field changed.
  const { data: saved } = await sb.from('requests')
    .select('tracking_number, shipping_company, tracking_url')
    .eq('id', requestId).maybeSingle();
  const merged = { ...(saved || {}), ...cols };
  const num = merged.tracking_number || '';
  const manual = merged.tracking_url || '';
  cols.tracking_redirect_url = (num || manual)
    ? getTrackingUrl(merged.shipping_company || '', num, manual)
    : null;

  const { error } = await sb.from('requests').update(cols).eq('id', requestId);
  if (error) throw error;
  return true;
}

// Upload the pre-shipping video / a managed document to the managed-media bucket.
export async function uploadManagedMedia(requestId, file) {
  const ext = ((file?.name || '').split('.').pop() || 'mp4').toLowerCase().replace(/[^a-z0-9]/g, '') || 'mp4';
  const path = `${requestId}/${Date.now()}.${ext}`;
  const up = await sb.storage.from('managed-media').upload(path, file, { contentType: file.type || 'video/mp4', upsert: true });
  if (up.error) throw up.error;
  return sb.storage.from('managed-media').getPublicUrl(path).data.publicUrl;
}
